import { useEffect, useMemo, useRef, useState } from 'react'
import { getNewsRedirectUrl, sortNewsByNewest } from '@/lib/data/news'
import { publicAsset } from '@/components/navigation/navigationAssets'
import type { NormalizedNewsItem } from '@/types/news'

interface HomeNewsSectionProps {
  variant: 'sidebar' | 'feature' | 'home'
  news: NormalizedNewsItem[]
  status: 'loading' | 'ready' | 'error'
  infiniteScroll?: boolean
}

export function HomeNewsSection({ news, status, variant, infiniteScroll = false }: HomeNewsSectionProps) {
  if (infiniteScroll && variant === 'feature') {
    return <InfiniteNewsSection news={news} status={status} />
  }
  if (variant === 'sidebar') {
    return (
      <aside className="home-latest" aria-labelledby="home-latest-title">
        <header>
          <h2 id="home-latest-title">Lo Último</h2>
          <a href={getNewsRedirectUrl()}>Ver todas →</a>
        </header>
        <NewsStatus status={status} news={news} />
        <div className="home-latest__list">
          {news.slice(0, 3).map((item) => (
            <NewsListItem item={item} key={item.id} />
          ))}
        </div>
      </aside>
    )
  }

  if (variant === 'home') {
    const featured = news[0]
    const secondary = news.slice(1, 4)

    return (
      <section className="home-news-feature home-news-feature--landing" aria-labelledby="home-news-title">
        <header className="home-section-header">
          <h2 id="home-news-title">Noticias</h2>
        </header>
        <NewsStatus status={status} news={news} />
        {featured ? <NewsCard item={featured} size="principal" /> : null}
        <div className="home-news-feature__secondary">
          {secondary.map((item) => <NewsCard item={item} size="small" key={item.id} />)}
        </div>
      </section>
    )
  }

  const sectionNews = news.length > 4 ? news.slice(4) : news
  const featured = sectionNews.slice(0, 2)
  const secondary = sectionNews.slice(2, 6)

  return (
    <section className="home-news-feature" aria-labelledby="home-news-title">
      <header className="home-section-header">
        <h2 id="home-news-title">Noticias</h2>
        <a href={getNewsRedirectUrl()}>Ver más noticias -&gt;</a>
      </header>
      <NewsStatus status={status} news={news} />

      <div className="home-news-feature__grid">
        {featured.map((item) => (
          <NewsCard item={item} size="large" key={item.id} />
        ))}
      </div>

      <div className="home-news-feature__secondary">
        {secondary.map((item) => (
          <NewsCard item={item} size="small" key={item.id} />
        ))}
      </div>
    </section>
  )
}

function InfiniteNewsSection({ news, status }: Pick<HomeNewsSectionProps, 'news' | 'status'>) {
  const sortedNews = useMemo(() => sortNewsByNewest(news), [news])
  // Remount pagination when the source changes, including its order/dates.
  const sourceKey = JSON.stringify(sortedNews.map((item) => [item.id, item.publishedAt]))
  return <PaginatedNews key={sourceKey} news={sortedNews} status={status} />
}

function PaginatedNews({ news, status }: Pick<HomeNewsSectionProps, 'news' | 'status'>) {
  const [visibleCount, setVisibleCount] = useState(6)
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const hasMore = visibleCount < news.length

  useEffect(() => {
    if (status !== 'ready' || !hasMore || !loadMoreRef.current || !('IntersectionObserver' in window)) return
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return
      observer.disconnect()
      setVisibleCount((count) => Math.min(count + 6, news.length))
    })
    observer.observe(loadMoreRef.current)
    return () => observer.disconnect()
  }, [hasMore, news.length, status, visibleCount])

  return (
    <section className="home-news-feature home-news-feature--infinite" aria-labelledby="home-news-title">
      <header className="home-section-header">
        <h2 id="home-news-title">Noticias</h2>
        <a href={getNewsRedirectUrl()}>Ver más noticias -&gt;</a>
      </header>
      <NewsStatus status={status} news={news} />
      <div className="home-news-feature__grid">
        {news.slice(0, 2).map((item) => <NewsCard item={item} size="large" key={item.id} />)}
      </div>
      <div className="home-news-feature__secondary">
        {news.slice(2, visibleCount).map((item) => <NewsCard item={item} size="small" key={item.id} />)}
      </div>
      {status === 'ready' && news.length > 0 && (
        <div className="news-pagination" ref={loadMoreRef}>
          <p role="status" aria-live="polite">
            Mostrando {Math.min(visibleCount, news.length)} de {news.length} noticias
          </p>
          {hasMore && (
            <button type="button" onClick={() => setVisibleCount((count) => Math.min(count + 6, news.length))}>
              Cargar más noticias
            </button>
          )}
        </div>
      )}
    </section>
  )
}

function NewsStatus({
  status,
  news,
}: {
  status: 'loading' | 'ready' | 'error'
  news: NormalizedNewsItem[]
}) {
  if (status === 'loading') {
    return <p className="home-news-status" role="status">Cargando noticias...</p>
  }

  if (status === 'error') {
    return <p className="home-news-status" role="status">No se pudo cargar la fuente de noticias.</p>
  }

  if (!news.length) {
    return <p className="home-news-status" role="status">No hay noticias disponibles en la fuente.</p>
  }

  return null
}

function NewsListItem({ item }: { item: NormalizedNewsItem }) {
  const placeholder = publicAsset('images/placeholders/no-image.jpg')

  return (
    <a className="home-latest-item" href={item.externalUrl} rel="noreferrer" target="_blank">
      <img src={item.imageUrl ?? placeholder} alt="" loading="lazy" decoding="async" />
      <span>
        <strong>{item.title}</strong>
        <small>{item.category}</small>
      </span>
      <b aria-hidden="true">›</b>
    </a>
  )
}

export function NewsCard({
  item,
  size,
}: {
  item: NormalizedNewsItem
  size: 'large' | 'small' | 'principal'
}) {
  const placeholder = publicAsset('images/placeholders/no-image.jpg')
  const imageUrl = item.imageUrl ?? placeholder

  return (
    <a
      className={`home-news-card home-news-card--${size}`}
      href={item.externalUrl}
      rel="noreferrer"
      target="_blank"
    >
      <img
        src={imageUrl}
        alt=""
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={(event) => {
          const placeholderUrl = new URL(placeholder, window.location.href).href

          if (event.currentTarget.src !== placeholderUrl) {
            event.currentTarget.src = placeholder
          }
        }}
      />
      <span>
        <small>{item.category}</small>
        <strong>{item.title}</strong>
      </span>
    </a>
  )
}
